import React, { useState } from 'react'
import AgTechTourHelpButton from '@agtech/web/components/App/Tour/AgTechTourHelpButton';
import { ApplyTaskTemplateDialogTourConfig } from './ApplyTaskTemplateDialogTourConfig';
import { useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import { AgTechButtonProps, AgTechButton, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons';
import AgTechFormLookup from '@agtech/web/components/Forms/Controls/Lookups/AgTechFormLookup';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { TaskTemplateDataGetTemplatesQuery } from '@sales/data/taskTemplates/TaskTemplateQueries';
import { useApplyTaskTemplateToSaleAction } from '@sales/data/taskTemplates/TaskTemplateDataActions';

export type ApplyTaskTemplateDialogProps = {
    saleId: number
}

type ApplyTaskTemplateFormData = {
    templateId: number | undefined
}

const ApplyTaskTemplateDialog = (props: ApplyTaskTemplateDialogProps) => {
    const dialogQuery = useAgTechQuery({
        query: TaskTemplateDataGetTemplatesQuery
    });

    const templatesQuery = useAgTechQuery({
        query: TaskTemplateDataGetTemplatesQuery,
        projection: templates => Object.values(templates)
    });

    const applyAction = useApplyTaskTemplateToSaleAction();

    return (
        <AgTechFormDialog
            width={450}
            structure={() => ({
                header: {
                    header: 'Apply Task Template',
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={ApplyTaskTemplateDialogTourConfig} />
                        }
                    }
                },
                submitButtonText: 'Apply',
            })}
            dialogData={{
                query: dialogQuery,
                getDialogData: (): ApplyTaskTemplateFormData => ({ templateId: undefined })
            }}
            dialogAction={{
                action: applyAction,
                getActionData: d => ({ templateId: d.templateId ?? 0, saleId: props.saleId })
            }}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <div id='apply-template-select'>
                    <AgTechFormRow>
                        <AgTechFormLookup
                            entityType={formContext.entity}
                            control={{
                                caption: 'Template',
                                width: 100
                            }}
                            selection={{
                                selectionControlTitle: 'Task Templates',
                                data: {
                                    itemQuery: templatesQuery,
                                    selectedItemId: formContext.entity.templateId?.toString(),
                                    searchItems: (item, filter) => item.name.includes(filter),
                                },
                                items: {
                                    itemText: t => t.name,
                                    itemHeight: 40,
                                    itemRendering: t => <div className='px-2 py-1'>{t.name}</div>,
                                },
                                behavior: {
                                    onItemSelected: async (e, selectedTemplate) => {
                                        return {
                                            ...formContext.entity,
                                            templateId: selectedTemplate?.id
                                        };
                                    },
                                },
                                popup: {
                                    height: 240,
                                    width: 360
                                }
                            }}
                        />
                    </AgTechFormRow>
                    </div>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
}

ApplyTaskTemplateDialog.Button = (props: ApplyTaskTemplateDialogProps & AgTechButtonProps) => (
    <AgTechButton
        text='Apply Task Template'
        icon='fas fa-clipboard-list'
        {...props}
        action={e => ApplyTaskTemplateDialog.Open(e, props)}
    />
);

ApplyTaskTemplateDialog.Open = (e: AgTechClickEventHandler, props: ApplyTaskTemplateDialogProps) => e.dialogs.openDialog({
    dialog: () => <ApplyTaskTemplateDialog saleId={props.saleId} />
});

export default ApplyTaskTemplateDialog;
